import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, Search, UserPlus, Music, Megaphone, UtensilsCrossed, Star, Gamepad2, CalendarDays, Lock, Unlock, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_LIST = [
  { key: 'song_requests', label: 'Songs', Icon: Music, color: '#FF007F' },
  { key: 'shoutouts', label: 'Shoutouts', Icon: Megaphone, color: '#39FF14' },
  { key: 'menus', label: 'Menu', Icon: UtensilsCrossed, color: '#00FFFF' },
  { key: 'reviews', label: 'Reviews', Icon: Star, color: '#FFD700' },
  { key: 'games', label: 'Games', Icon: Gamepad2, color: '#FF8C00' },
  { key: 'events', label: 'Events', Icon: CalendarDays, color: '#FF007F' },
];

const defaultFeatures = () => ({ song_requests: true, shoutouts: true, menus: false, reviews: true, games: false, events: false });

const AdminClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    phone: '',
    features: defaultFeatures(),
  });

  const [featuresBusyId, setFeaturesBusyId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(() => ({
      name: '',
      email: '',
      password: '',
      company: '',
      phone: '',
      features: { song_requests: true, shoutouts: true, menus: false, reviews: true, games: false, events: false },
    }));
    setEditingClient(null);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        email: client.email || '',
        password: '',
        company: client.company || '',
        phone: client.phone || '',
        features: { ...defaultFeatures(), ...(client.features || {}) },
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!editingClient && !formData.password) {
      toast.error('Password is required for new clients');
      return;
    }

    try {
      if (editingClient) {
        const updateData = {
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          features: formData.features,
        };
        await axios.put(`${API}/admin/clients/${editingClient.id}`, updateData);
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/admin/clients`, formData);
        toast.success('Client created');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Failed to save client:', error);
      const message = error.response?.data?.detail || 'Failed to save client';
      toast.error(message);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to deactivate this client?')) return;
    
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`);
      toast.success('Client deactivated');
      fetchClients();
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to deactivate client');
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination — slice the filtered list. Reset to page 1 whenever the search
  // changes so the user never lands on an empty page after typing.
  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedClients = filteredClients.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [searchQuery]);

  const toggleFeature = async (client, key, value) => {
    setFeaturesBusyId(`${client.id}:${key}`);
    try {
      const res = await axios.put(`${API}/admin/clients/${client.id}`, {
        features: { ...(client.features || defaultFeatures()), [key]: value },
      });
      setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, features: res.data.features } : c)));
    } catch {
      toast.error('Failed to update feature');
    } finally {
      setFeaturesBusyId(null);
    }
  };

  // ---- Premium playback gating handlers ----
  const togglePlaybackLock = async (client) => {
    const nextLocked = !client.playback_locked;
    const verb = nextLocked ? 'lock' : 'unlock';
    if (nextLocked && !window.confirm(`Lock playback for ${client.name || client.email}? They will see a "Playback Locked" screen on the DJ Console until you unlock them.`)) return;
    try {
      await axios.post(`${API}/admin/clients/${client.id}/playback-lock`, {
        locked: nextLocked,
        reason: nextLocked ? 'Locked by Super Admin' : null,
      });
      toast.success(`Playback ${verb}ed for ${client.name || client.email}`);
      // Optimistic update — if we just UNLOCKED, also clear the flag client-side
      setClients((prev) => prev.map((c) => c.id === client.id ? ({
        ...c,
        playback_locked: nextLocked,
        playback_lock_reason: nextLocked ? 'Locked by Super Admin' : null,
        premium_flag_raised_at: nextLocked ? c.premium_flag_raised_at : null,
        premium_flag_reason: nextLocked ? c.premium_flag_reason : null,
        premium_verified_at: nextLocked ? c.premium_verified_at : new Date().toISOString(),
      }) : c));
    } catch (e) {
      toast.error(`Failed to ${verb} playback`);
    }
  };

  const markVerified = async (client) => {
    try {
      const r = await axios.post(`${API}/admin/clients/${client.id}/mark-verified`);
      toast.success(`Marked ${client.name || client.email} as Premium verified`);
      setClients((prev) => prev.map((c) => c.id === client.id ? ({
        ...c,
        premium_flag_raised_at: null,
        premium_flag_reason: null,
        premium_verified_at: r.data.verified_at,
      }) : c));
    } catch (e) {
      toast.error('Failed to mark as verified');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 overflow-y-auto content-scroll" data-testid="admin-clients-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">Clients</h1>
            <p className="text-gray-400">Manage your client accounts</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-black rounded-full"
                data-testid="add-client-button"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Client name"
                    className="bg-[#1A1A1A] border-white/10"
                    data-testid="client-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="client@example.com"
                    disabled={!!editingClient}
                    className="bg-[#1A1A1A] border-white/10 disabled:opacity-50"
                    data-testid="client-email-input"
                  />
                </div>

                {!editingClient && (
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Initial password"
                      className="bg-[#1A1A1A] border-white/10"
                      data-testid="client-password-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Company name"
                    className="bg-[#1A1A1A] border-white/10"
                    data-testid="client-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    className="bg-[#1A1A1A] border-white/10"
                    data-testid="client-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Features</Label>
                  <div className="space-y-2 bg-[#1A1A1A] border border-white/10 rounded-xl p-3">
                    {FEATURE_LIST.map(({ key, label, Icon, color }) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm text-gray-200">
                          <Icon className="w-4 h-4" style={{ color }} /> {label}
                        </span>
                        <Switch
                          checked={!!formData.features[key]}
                          onCheckedChange={(v) => setFormData({ ...formData, features: { ...formData.features, [key]: v } })}
                          data-testid={`form-feature-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-[#FF007F] hover:bg-[#CC0066] text-black"
                    data-testid="save-client-button"
                  >
                    {editingClient ? 'Update' : 'Create'} Client
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients..."
            className="bg-[#121212] border-white/10 pl-12 h-12"
            data-testid="search-clients-input"
          />
        </div>

        {/* Clients Table */}
        <div className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-gray-400">Client</TableHead>
                <TableHead className="text-gray-400">Company</TableHead>
                <TableHead className="text-gray-400">Features</TableHead>
                <TableHead className="text-gray-400">Playback</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-400">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-400">
                    No clients found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedClients.map((client, index) => (
                  <TableRow key={client.id || index} className="border-white/5" data-testid={`client-row-${client.id}`}>
                    <TableCell>
                      <Link
                        to={`/admin/clients/${client.id}/schedule`}
                        className="flex items-center gap-3 hover:opacity-80 transition group"
                        data-testid={`client-name-link-${client.id}`}
                        title="View weekly schedule"
                      >
                        <div className="w-10 h-10 rounded-full bg-[#FF007F]/20 flex items-center justify-center text-[#FF007F] font-medium">
                          {client.name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="font-medium text-white group-hover:text-[#FF007F] transition-colors">{client.name}</p>
                          <p className="text-sm text-gray-400">{client.email}</p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-300">{client.company || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {FEATURE_LIST.map(({ key, label, Icon, color }) => {
                          const on = !!(client.features?.[key]);
                          const busy = featuresBusyId === `${client.id}:${key}`;
                          return (
                            <button
                              key={key}
                              onClick={() => toggleFeature(client, key, !on)}
                              disabled={busy}
                              title={`${label}: ${on ? 'On' : 'Off'} (click to toggle)`}
                              className={`flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold tracking-wider transition-all ${
                                on
                                  ? 'bg-black/40 text-white'
                                  : 'bg-transparent text-gray-600 border-white/10'
                              }`}
                              style={on ? { borderColor: `${color}55`, color } : {}}
                              data-testid={`feature-toggle-${client.id}-${key}`}
                            >
                              <Icon className="w-3 h-3" />
                              {label.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5" data-testid={`playback-status-${client.id}`}>
                        {client.playback_locked ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40 text-[#FF007F] text-[10px] font-bold tracking-wider w-fit"
                            title={client.playback_lock_reason || 'Locked'}
                          >
                            <Lock className="w-3 h-3" /> LOCKED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 text-[#39FF14] text-[10px] font-bold tracking-wider w-fit">
                            <Unlock className="w-3 h-3" /> UNLOCKED
                          </span>
                        )}
                        {client.premium_flag_raised_at && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF8C00]/15 border border-[#FF8C00]/40 text-[#FF8C00] text-[10px] font-bold tracking-wider w-fit"
                            title={client.premium_flag_reason || 'Premium flag raised'}
                            data-testid={`premium-flag-${client.id}`}
                          >
                            <ShieldAlert className="w-3 h-3" /> FLAGGED
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`tag text-xs ${client.is_active !== false ? 'tag-green' : 'tag-red'}`}>
                        {client.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePlaybackLock(client)}
                          className={`h-8 px-3 rounded-full text-[11px] font-bold tracking-wider ${
                            client.playback_locked
                              ? 'hover:bg-[#39FF14]/10 text-[#39FF14]'
                              : 'hover:bg-[#FF007F]/10 text-[#FF007F]'
                          }`}
                          title={client.playback_locked ? 'Unlock playback' : 'Lock playback'}
                          data-testid={`toggle-lock-${client.id}`}
                        >
                          {client.playback_locked ? (
                            <><Unlock className="w-3.5 h-3.5 mr-1" /> UNLOCK</>
                          ) : (
                            <><Lock className="w-3.5 h-3.5 mr-1" /> LOCK</>
                          )}
                        </Button>
                        {client.premium_flag_raised_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markVerified(client)}
                            className="hover:bg-[#39FF14]/10 text-[#39FF14] h-8 px-3 rounded-full text-[11px] font-bold tracking-wider"
                            title="Clear the auto-raised Premium flag (after investigating)"
                            data-testid={`mark-verified-${client.id}`}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> CLEAR FLAG
                          </Button>
                        )}
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="hover:bg-[#00FFFF]/10 text-[#00FFFF] h-8 px-3 rounded-full text-[11px] font-bold tracking-wider"
                          data-testid={`manage-menu-${client.id}`}
                          title="Manage this client's menu (white-glove)"
                        >
                          <Link to={`/admin/clients/${client.id}/menu`}>
                            <UtensilsCrossed className="w-3.5 h-3.5 mr-1" />
                            MENU
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(client)}
                          className="hover:bg-white/10"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil className="w-4 h-4 text-gray-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(client.id)}
                          className="hover:bg-red-500/20"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {filteredClients.length > 0 && (
          <div
            className="flex items-center justify-between mt-4 text-sm text-gray-400 px-2"
            data-testid="clients-pagination"
          >
            <span data-testid="clients-pagination-range">
              Showing{' '}
              <span className="text-white font-semibold">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredClients.length)}
              </span>
              {' '}of{' '}
              <span className="text-white font-semibold">{filteredClients.length}</span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border border-white/10 text-gray-300 hover:text-white disabled:opacity-30 rounded-full px-4"
                data-testid="clients-pagination-prev"
              >
                Prev
              </Button>
              <span className="text-gray-300 font-mono text-xs px-2">
                {safePage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="border border-white/10 text-gray-300 hover:text-white disabled:opacity-30 rounded-full px-4"
                data-testid="clients-pagination-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminClientsPage;
